/* eslint-disable react/no-children-prop */
import { CircularProgress, Grid } from '@mui/material';
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch, matchPath, useHistory, useLocation } from 'react-router-dom';
import { clearReturnUrl, getReturnUrl } from '../../common/helpers/returnUrl';

import AuthenticatedPaths from './AuthenticatedRoutes.paths';
import Dashboard from '../../dashboard/views';
import DashboardLayout from '../../common/layouts/Dashboard';
import NotFound from '../NotFound';
import PropTypes from 'prop-types';
import UnauthenticatedPaths from '../UnauthenticatedRoutes/UnauthenticatedRoutes.paths';

// Routes
const AccountRoutes = lazy(() => import('../../account/routes/Routes'));
const ApiKeysRoutes = lazy(() => import('../../apiKeys/routes/Routes'));
const AskAnalystRoutes = lazy(() => import('../../askAnalyst/routes'));
const AssessmentsRoutes = lazy(() => import('../../assessments/routes'));
const ConsentRoutesNewUx = lazy(() => import('../../consent/routes/Routes.jsx'));
const ConsumerSsoRoutes = lazy(() => import('../../consumerSso/routes/Routes.jsx'));
const DataMappingRoutes = lazy(() => import('../../dataMapping/routes'));
const DataStoresRoutes = lazy(() => import('../../dataStores/routes'));
const DocumentRoutes = lazy(() => import('../../documents/routes'));
const DomainsRoutes = lazy(() => import('../../domains/routes'));
const DSARActionItemRoutes = lazy(() => import('../../dsarActionItems/routes'));
const DSARFormsRoutes = lazy(() => import('../../dsarForms/routes'));
const DSARReportingRoutes = lazy(() => import('../../dsarReporting/routes'));
const DsarConsumerSsoRoutes = lazy(() => import('../../dsar/routes/ConsumerSsoRoutes.jsx'));
const DSARRequestsRoutes = lazy(() => import('../../dsarRequests/routes'));
const GDPRRoutes = lazy(() => import('../../gdpr/routes'));
const IntegrationsRoutes = lazy(() => import('../../integrations/routes'));
const LegislativeRoutes = lazy(() => import('../../legislation/routes'));
const LitigationRoutes = lazy(() => import('../../litigation/routes'));
const NotificationsRoutes = lazy(() => import('../../notifications/routes'));
const OrganizationsRoutes = lazy(() => import('../../organizations/routes'));
const PrivacyAdvisorRoutes = lazy(() => import('../../privacyAdvisor/routes'));
const ResourcesRoutes = lazy(() => import('../../resources/routes'));
const TrusthubRoutes = lazy(() => import('../../trusthub/routes'));
const UcRoutes = lazy(() => import('../../uc/routes'));
const UsersRoutes = lazy(() => import('../../users/routes'));
const VendorsRoutes = lazy(() => import('../../vendors/routes'));

const WebhooksRoutes = lazy(() => import('../../webhooks/routes'));

const FallbackLoader = () => (
    <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
    >
        <Grid item lg={6} xs={3}>
            <CircularProgress />
        </Grid>
    </Grid>
);

const AuthenticatedRoutes = ({
    isAuthenticated,
    userId,
    customerToken,
    fetchUserDetails,
    roles,
    hasPermissionToReadAssessments,
    hasPermissionToViewAssessments,
    hasPermissionToViewOrganizations,
}) => {
    const mounted = useRef(null);
    const history = useHistory();
    const routerLocation = useLocation();
    const [isLoadingDetails, setLoadingDetails] = useState(true);
    useEffect(() => {
        mounted.current = true;
        if (customerToken) {
            setLoadingDetails(true);
            fetchUserDetails(userId)
                .then(() => {
                    mounted.current && setLoadingDetails(false);
                })
                .catch(() => {
                    mounted.current && setLoadingDetails(false);
                });
        }
        return () => {
            mounted.current = false;
        };
    }, [customerToken, fetchUserDetails]);

    useEffect(() => {
        let redirect = false;
        if (roles.length > 0) {
            // Redirects are only necessary once the user has roles to determine permissions
            if (
                // Customer has Assessments flag
                hasPermissionToViewAssessments &&
                // User does not have Admin or Assessment Role
                !hasPermissionToReadAssessments &&
                matchPath(routerLocation.pathname, AuthenticatedPaths.ASSESSMENTS) &&
                !matchPath(routerLocation.pathname, AuthenticatedPaths.ASSESSMENT_ASSIGNMENTS)
            ) {
                // Redirect the user to Assignments if they are on Assessments and their role no longer allows
                redirect = AuthenticatedPaths.ASSESSMENT_ASSIGNMENTS;
            }
        }
        if (redirect) {
            history.replace(redirect);
        }
    }, [hasPermissionToReadAssessments, hasPermissionToViewAssessments]);

    // Route chunks are code-split behind `lazy()`. Webpack used to emit
    // `<link rel="prefetch">` for each (via `webpackPrefetch`) so they downloaded
    // during idle time; Vite has no equivalent and `modulepreload` only covers
    // the entry's static imports. Warm the highest-traffic routes on idle so the
    // first navigation into them resolves from cache. The specifiers must match
    // the `lazy()` imports above for the module cache to be shared.
    useEffect(() => {
        const warmCommonRoutes = () => {
            import('../../consent/routes/Routes.jsx');
            import('../../dsarRequests/routes');
            import('../../dataMapping/routes');
            import('../../vendors/routes');
        };
        const scheduleIdle = window.requestIdleCallback || (cb => setTimeout(cb, 2000));
        const cancelIdle = window.cancelIdleCallback || clearTimeout;
        const handle = scheduleIdle(warmCommonRoutes);
        return () => cancelIdle(handle);
    }, []);

    // Clear the return URL once we've landed on an authenticated page after
    // the full page reload. This prevents stale redirects on future refreshes.
    useEffect(() => {
        if (
            isAuthenticated &&
            !Object.values(UnauthenticatedPaths).some(x => matchPath(routerLocation.pathname, x))
        ) {
            clearReturnUrl();
        }
    }, [isAuthenticated, routerLocation.pathname]);

    if (
        isAuthenticated &&
        Object.values(UnauthenticatedPaths).some(x => matchPath(routerLocation.pathname, x))
    ) {
        // User is authenticated, but on an unauthenticated page (e.g. sign in flow).
        // Do a full page load so b2b.js is reloaded. This should help mitigate app
        // crashing after deployments.
        // Use non-destructive read — multiple Redux dispatches during login
        // can trigger several re-renders before the browser navigates away.
        // If we removed the URL on first read, subsequent renders would fall
        // back to DASHBOARD and the last window.location assignment wins.
        const returnUrl = getReturnUrl();
        window.location = returnUrl || AuthenticatedPaths.DASHBOARD;

        return <FallbackLoader />;
    }

    return isAuthenticated ? (
        <DashboardLayout>
            {isLoadingDetails ? (
                <FallbackLoader />
            ) : (
                <Suspense fallback={<FallbackLoader />}>
                    <Switch>
                        <Route path={AuthenticatedPaths.ACCOUNT} children={<AccountRoutes />} />
                        <Route path={AuthenticatedPaths.API_KEYS} children={<ApiKeysRoutes />} />
                        <Route
                            path={AuthenticatedPaths.ASK_ANALYST}
                            children={<AskAnalystRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.CONSENT}
                            children={<ConsentRoutesNewUx />}
                        />
                        <Route
                            path={AuthenticatedPaths.CONSUMER_SSO}
                            children={<ConsumerSsoRoutes />}
                        />
                        <Route
                            path={[
                                AuthenticatedPaths.DATA_MAPPING_SOURCES,
                                AuthenticatedPaths.DATA_MAPPING_APPLICATIONS,
                                AuthenticatedPaths.DATA_MAP,
                            ]}
                            children={<DataMappingRoutes />}
                        />
                        <Route
                            path={[AuthenticatedPaths.DATA_STORES]}
                            children={<DataStoresRoutes />}
                        />
                        <Route
                            path={[
                                AuthenticatedPaths.DOCUMENTS,
                                AuthenticatedPaths.DOCUMENT_CHANGES,
                            ]}
                            children={<DocumentRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.COMPLIANCE_CHECK_SITES}
                            children={<DomainsRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.DSAR_ACTION_ITEMS}
                            children={<DSARActionItemRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.DSAR_FORMS}
                            children={<DSARFormsRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.DSAR_REPORTING}
                            children={<DSARReportingRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.DSAR_REQUESTS}
                            children={<DSARRequestsRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.DSAR_CONSUMER_SSO}
                            children={<DsarConsumerSsoRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.NOTIFICATIONS}
                            children={<NotificationsRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.LEGISLATION}
                            children={<LegislativeRoutes />}
                        />
                        <Route
                            path={AuthenticatedPaths.LITIGATION}
                            children={<LitigationRoutes />}
                        />
                        {hasPermissionToViewOrganizations && (
                            <Route
                                path={AuthenticatedPaths.ORGANIZATIONS}
                                children={<OrganizationsRoutes />}
                            />
                        )}
                        <Route
                            path={AuthenticatedPaths.PRIVACY_ADVISOR}
                            children={<PrivacyAdvisorRoutes />}
                        />
                        <Route path={AuthenticatedPaths.UC} children={<UcRoutes />} />
                        <Route path={AuthenticatedPaths.USERS} children={<UsersRoutes />} />
                        <Route path={AuthenticatedPaths.VENDORS} children={<VendorsRoutes />} />
                        <Route path={AuthenticatedPaths.REPORTS_CONSENT}>
                            <Redirect to={AuthenticatedPaths.REPORTS_CONSENT_V2} />
                        </Route>
                        <Route path={AuthenticatedPaths.REPORTS_DATASUBJECT}>
                            <Redirect to={AuthenticatedPaths.REPORTS_DATASUBJECT_V2} />
                        </Route>
                        <Route
                            path={[
                                AuthenticatedPaths.ASSESSMENTS,
                                AuthenticatedPaths.ASSESSMENT_ASSIGNMENTS,
                            ]}
                            children={<AssessmentsRoutes />}
                        />
                        <Route path={AuthenticatedPaths.RESOURCES} children={<ResourcesRoutes />} />
                        <Route path={AuthenticatedPaths.TRUSTHUB} children={<TrusthubRoutes />} />
                        <Route path={AuthenticatedPaths.GDPR} children={<GDPRRoutes />} />
                        <Route
                            path={AuthenticatedPaths.INTEGRATIONS}
                            children={<IntegrationsRoutes />}
                        />
                        <Route path={AuthenticatedPaths.WEBHOOKS} children={<WebhooksRoutes />} />
                        <Route
                            path={AuthenticatedPaths.REDIRECT_CONSENT_MANAGER}
                            children={<Redirect to={AuthenticatedPaths.CONSENT_MANAGER} />}
                        />
                        <Route exact path={AuthenticatedPaths.DASHBOARD} children={<Dashboard />} />
                        <Route exact path={AuthenticatedPaths.NOT_FOUND} children={<NotFound />} />
                        <Route
                            exact
                            path="/"
                            children={<Redirect to={AuthenticatedPaths.DASHBOARD} />}
                        />
                        <Route children={<Redirect to={AuthenticatedPaths.NOT_FOUND} />} />
                    </Switch>
                </Suspense>
            )}
        </DashboardLayout>
    ) : null;
};

AuthenticatedRoutes.propTypes = {
    customerToken: PropTypes.string,
    fetchUserDetails: PropTypes.func,
    hasPermissionToReadAssessments: PropTypes.bool,
    hasPermissionToViewAssessments: PropTypes.bool,
    hasPermissionToViewOrganizations: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    roles: PropTypes.array,
    userId: PropTypes.string,
};

export default AuthenticatedRoutes;
